import { Component, computed, input, signal } from '@angular/core'
import { MetricModel } from 'src/app/shared/models/metric.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { ObstaclesMetrics, getMetricFromMap } from '../../../obstacle/enums/obstacle-metrics.enum'
import { Observable, Subject, takeUntil } from 'rxjs'
import { ProviderInfo } from '../../model/provider-info.model'

@Component({
  selector: 'app-obstacle-layer-legend',
  templateUrl: './obstacle-layer-legend.component.html',
  standalone: false,
})
export class ObstacleLayerLegendComponent {
  private cleanUp$ = new Subject<void>()
  public metrics: string | undefined
  public isProvider1BDVLayerVisible = signal(false)
  public isProvider1AccidentLayerVisible = signal(false)
  public isProvider1ConstructionSitesLayerVisible = signal(false)
  public isProvider2BDVLayerVisible = signal(false)
  public isProvider2AccidentLayerVisible = signal(false)
  public isProvider2ConstructionSitesLayerVisible = signal(false)
  public isProvider3AccidentLayerVisible = signal(false)
  public isProvider3BDVLayerVisible = signal(false)
  public isProvider3ConstructionSitesLayerVisible = signal(false)
  public isProvider4BDVLayerVisible = signal(false)
  public isProvider4AccidentsLayerVisible = signal(false)
  public isProvider6AccidentLayerVisible = signal(false)
  public isProvider6BDVLayerVisible = signal(false)
  public isProvider7AccidentLayerVisible = signal(false)
  public isProvider7BDVLayerVisible = signal(false)
  public isProvider8AccidentLayerVisible = signal(false)
  public isProvider8BDVLayerVisible = signal(false)
  public isProvider8WdwLayerVisible = signal(false)
  public isProvider8SvLayerVisible = signal(false)
  public isProvider8CsLayerVisible = signal(false)
  public isProvider9SvLayerVisible = signal(false)
  public isProvider9CsLayerVisible = signal(false)
  public isLayerSelectionVisible = false
  public isVAWarningLayerVisible = signal(false)
  public isBDVWarningLayerVisible = signal(false)
  public isCSWarningLayerVisible = signal(false)

  public obstacleMetric = signal<MetricModel | undefined>(undefined)
  public provider1Metric = signal<MetricModel | undefined>(undefined)
  public provider2Metric = signal<MetricModel | undefined>(undefined)
  public provider3Metric = signal<MetricModel | undefined>(undefined)
  public provider4Metric = signal<MetricModel | undefined>(undefined)
  public provider6Metric = signal<MetricModel | undefined>(undefined)
  public provider7Metric = signal<MetricModel | undefined>(undefined)
  public provider8Metric = signal<MetricModel | undefined>(undefined)
  public provider9Metric = signal<MetricModel | undefined>(undefined)
  public providerInfo$ = input.required<Observable<ProviderInfo>>()

  public isOneProvider1LayerVisible = computed(
    () =>
      this.isProvider1AccidentLayerVisible() ||
      this.isProvider1BDVLayerVisible() ||
      this.isProvider1ConstructionSitesLayerVisible(),
  )

  public isOneProvider2LayerVisible = computed(
    () =>
      this.isProvider2AccidentLayerVisible() ||
      this.isProvider2BDVLayerVisible() ||
      this.isProvider2ConstructionSitesLayerVisible(),
  )

  public isOneProvider3LayerVisible = computed(
    () =>
      this.isProvider3AccidentLayerVisible() ||
      this.isProvider3BDVLayerVisible() ||
      this.isProvider3ConstructionSitesLayerVisible(),
  )

  public isOneProvider4LayerVisible = computed(
    () => this.isProvider4BDVLayerVisible() || this.isProvider4AccidentsLayerVisible(),
  )

  public isOneProvider6LayerVisible = computed(
    () => this.isProvider6AccidentLayerVisible() || this.isProvider6BDVLayerVisible(),
  )

  public isOneProvider7LayerVisible = computed(
    () => this.isProvider7AccidentLayerVisible() || this.isProvider7BDVLayerVisible(),
  )

  public isOneProvider8LayerVisible = computed(
    () =>
      this.isProvider8AccidentLayerVisible() ||
      this.isProvider8BDVLayerVisible() ||
      this.isProvider8CsLayerVisible() ||
      this.isProvider8SvLayerVisible() ||
      this.isProvider8WdwLayerVisible(),
  )

  public isOneProvider9LayerVisible = computed(
    () => this.isProvider9CsLayerVisible() || this.isProvider9SvLayerVisible(),
  )

  public isOneWarningLayerVisible = computed(
    () =>
      this.isVAWarningLayerVisible() ||
      this.isBDVWarningLayerVisible() ||
      this.isCSWarningLayerVisible(),
  )

  constructor(private appState: AppStateService) {
    this.evaluateQueryParameters()

    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateQueryParameters()
    })
  }
  ngOnDestroy(): void {
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  evaluateQueryParameters() {
    const layers = this.appState.getState().layers

    this.isProvider1AccidentLayerVisible.set(!!layers?.includes(Layers.Provider1Accidents.name))
    this.isProvider1BDVLayerVisible.set(!!layers?.includes(Layers.Provider1Bdv.name))
    this.isProvider1ConstructionSitesLayerVisible.set(
      !!layers?.includes(Layers.Provider1ConstructionSites.name),
    )

    this.isProvider2AccidentLayerVisible.set(!!layers?.includes(Layers.Provider2Accidents.name))
    this.isProvider2BDVLayerVisible.set(!!layers?.includes(Layers.Provider2Bdv.name))
    this.isProvider2ConstructionSitesLayerVisible.set(
      !!layers?.includes(Layers.Provider2ConstructionSites.name),
    )

    this.isProvider3AccidentLayerVisible.set(!!layers?.includes(Layers.Provider3Accidents.name))
    this.isProvider3BDVLayerVisible.set(!!layers?.includes(Layers.Provider3Bdv.name))
    this.isProvider3ConstructionSitesLayerVisible.set(
      !!layers?.includes(Layers.Provider3ConstructionSites.name),
    )

    this.isProvider4AccidentsLayerVisible.set(!!layers?.includes(Layers.Provider4Accidents.name))
    this.isProvider4BDVLayerVisible.set(!!layers?.includes(Layers.Provider4Bdv.name))

    this.isProvider6AccidentLayerVisible.set(!!layers?.includes(Layers.Provider6Accidents.name))
    this.isProvider6BDVLayerVisible.set(!!layers?.includes(Layers.Provider6Bdv.name))

    this.isProvider7AccidentLayerVisible.set(!!layers?.includes(Layers.Provider7Accidents.name))
    this.isProvider7BDVLayerVisible.set(!!layers?.includes(Layers.Provider7Bdv.name))

    this.isProvider8AccidentLayerVisible.set(!!layers?.includes(Layers.Provider8Accidents.name))
    this.isProvider8BDVLayerVisible.set(!!layers?.includes(Layers.Provider8Bdv.name))
    this.isProvider8WdwLayerVisible.set(!!layers?.includes(Layers.Provider8Wdw.name))
    this.isProvider8SvLayerVisible.set(!!layers?.includes(Layers.Provider8Sv.name))
    this.isProvider8CsLayerVisible.set(!!layers?.includes(Layers.Provider8Cs.name))

    this.isProvider9CsLayerVisible.set(!!layers?.includes(Layers.Provider9Cs.name))
    this.isProvider9SvLayerVisible.set(!!layers?.includes(Layers.Provider9Sv.name))

    this.isVAWarningLayerVisible.set(!!layers?.includes(Layers.VAWarning.name))
    this.isBDVWarningLayerVisible.set(!!layers?.includes(Layers.BDVWarning.name))
    this.isCSWarningLayerVisible.set(!!layers?.includes(Layers.CSWarning.name))

    this.obstacleMetric.set(
      this.isBDVWarningLayerVisible()
        ? getMetricFromMap(ObstaclesMetrics.Obstacles_BDV)
        : getMetricFromMap(ObstaclesMetrics.Obstacles_Accidents),
    )
    this.provider1Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider1))
    this.provider2Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider2))
    this.provider3Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider3))
    this.provider4Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider4))
    this.provider6Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider6))
    this.provider7Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider7))
    this.provider8Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider8))
    this.provider9Metric.set(getMetricFromMap(ObstaclesMetrics.Obstacles_Provider9))
  }
}
